import { APP_LOCAL_DATE_FORMAT } from "@/config/constants";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoFile } from "@/shared/model/edo-file.model";
import { IEdoUnit } from "@/shared/model/edo-unit.model";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
} from "@/components/ui";
import {
  Bookmark,
  ChevronDown,
  ChevronUp,
  Edit,
  FileText,
  Eye,
  Download,
  Trash2,
} from "lucide-react";
import axios from "axios";
import "moment/locale/it";
import React, { useEffect, useState } from "react";
import { TextFormat, Translate } from "react-jhipster";
import { UnitRow } from "./UnitRow";

export interface CapitoloRowProps {
  chapter?: IEdoChapter;
  isLastElement?: boolean;
  handleOpenEditCapitoloDialog?: (entity: IEdoChapter) => any;
  handleOpenEditChapterUnitsDialog?: (entity: IEdoChapter) => any;
  handleOpenDeleteCapitoloDialog?: (entity: IEdoChapter) => any;
  handleOpenViewUnitDialog: (unit: IEdoUnit) => void;
}

export const CapitoloRow = (props: CapitoloRowProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [units, setUnits] = useState<IEdoUnit[]>([]);
  const [files, setFiles] = useState<IEdoFile[]>([]);
  const [loadingUnits, setLoadingUnits] = useState<boolean>(true);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);

  const {
    chapter,
    isLastElement,
    handleOpenEditCapitoloDialog,
    handleOpenDeleteCapitoloDialog,
    handleOpenViewUnitDialog,
    handleOpenEditChapterUnitsDialog,
  } = props;

  const handleOpen = () => {
    setOpen(!open);
  };

  const openFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/view`, "_blank");
  };

  const downloadFile = (file: IEdoFile) => {
    window.open(`/api/files/${file.id}/download`, "_blank");
  };

  useEffect(() => {
    const loadUnits = async (chapterItem: IEdoChapter) => {
      setLoadingUnits(true);
      const response = await axios.get(`/chapters/${chapterItem.id}/units`);
      setUnits(response.data.data);
      setLoadingUnits(false);
    };
    const loadFiles = async (chapterItem: IEdoChapter) => {
      setLoadingFiles(true);
      const response = await axios.get(`/chapters/${chapterItem.id}/files`);
      setFiles(response.data.data);
      setLoadingFiles(false);
    };

    if (chapter && open) {
      loadUnits(chapter);
      loadFiles(chapter);
    }
  }, [chapter, open]);

  useEffect(() => {
    if (!chapter || loadingUnits || loadingFiles) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [chapter, loadingUnits, loadingFiles]);

  const getContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-6">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      );
    }

    if (!files.length && !units.length) {
      return (
        <div className="px-6 py-5 text-center">
          <p className="text-sm text-[var(--color-mockup-text-light)]">
            Nessun materiale contenuto nel capitolo
          </p>
        </div>
      );
    }

    return (
      <>
        {files.length > 0 && (
          <List className="border-t border-[var(--color-mockup-border)] pl-4 sm:pl-6">
            {files.map((file) => (
              <ListItem
                key={file.id}
                className="flex cursor-pointer items-center gap-3 px-2 py-2.5 transition-colors hover:bg-primary/5"
                onClick={() => openFile(file)}
              >
                <ListItemIcon className="shrink-0">
                  {getFileAvatar(file.fileType)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span className="text-sm font-medium text-[var(--color-mockup-text)]">
                      {file.originalFileName}
                    </span>
                  }
                  secondary={
                    <span className="text-xs text-[var(--color-mockup-text-light)]">
                      <TextFormat
                        value={file.createdAt}
                        type="date"
                        format={APP_LOCAL_DATE_FORMAT}
                      />
                    </span>
                  }
                  className="min-w-0 flex-1"
                />
                <ListItemSecondaryAction
                  className="ml-2 flex shrink-0 gap-0.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconButton
                    onClick={() => openFile(file)}
                    aria-label="Apri file"
                    className="w-7 h-7 rounded-full text-[var(--color-mockup-text-light)] hover:text-primary hover:bg-primary/10"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton
                    onClick={() => downloadFile(file)}
                    aria-label="Scarica file"
                    className="w-7 h-7 rounded-full text-[var(--color-mockup-text-light)] hover:text-primary hover:bg-primary/10"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
        {units.length > 0 && (
          <List className="border-t border-[var(--color-mockup-border)]">
            {units.map((unit) => (
              <UnitRow
                nested
                key={unit.id}
                unit={unit}
                viewUnit={() => handleOpenViewUnitDialog(unit)}
                editUnit={null}
                deleteUnit={null}
              />
            ))}
          </List>
        )}
      </>
    );
  };

  if (!chapter) return null;

  return (
    <>
      <ListItem
        className="flex cursor-pointer items-center gap-3 px-5 py-3.5 transition-colors hover:bg-primary/5"
        onClick={handleOpen}
      >
        <ListItemText
          primary={
            <span className="text-sm font-semibold text-[var(--color-mockup-text)]">
              {chapter.title}
            </span>
          }
          secondary={
            <span className="text-xs text-[var(--color-mockup-text-light)]">
              <TextFormat
                value={chapter.createdAt}
                type="date"
                format={APP_LOCAL_DATE_FORMAT}
              />
            </span>
          }
          className="min-w-0 flex-1"
        />
        <div className={`flex h-6 w-6 items-center justify-center rounded-full transition-colors ${open ? 'bg-primary/15 text-primary' : 'text-[var(--color-mockup-text-light)]'}`}>
          {open ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </ListItem>
      {open && (
        <div className="border-t border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/40">
          {getContent()}
          {(handleOpenEditCapitoloDialog != null ||
            handleOpenDeleteCapitoloDialog != null) && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--color-mockup-border)] px-5 py-3">
              {handleOpenEditCapitoloDialog != null && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 rounded-full border border-[var(--color-mockup-border)] bg-surface px-3 py-1.5 text-xs font-bold text-[var(--color-mockup-text)] transition-colors hover:border-primary/30 hover:bg-primary/5">
                      <Edit className="h-3.5 w-3.5" />
                      <Translate contentKey="entity.action.edit" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        handleOpenEditCapitoloDialog(chapter);
                      }}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Modifica informazioni e documenti
                    </DropdownMenuItem>
                    {handleOpenEditChapterUnitsDialog != null && (
                      <DropdownMenuItem
                        onClick={() => {
                          handleOpenEditChapterUnitsDialog(chapter);
                        }}
                      >
                        <Bookmark className="mr-2 h-4 w-4" />
                        Modifica unità didattiche
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {handleOpenDeleteCapitoloDialog != null && (
                <button
                  className="flex items-center gap-1.5 rounded-full border border-[var(--color-mockup-coral)]/20 bg-[var(--color-mockup-coral-light)] px-3 py-1.5 text-xs font-bold text-[var(--color-mockup-coral)] transition-colors hover:bg-[var(--color-mockup-coral)]/15"
                  onClick={() => handleOpenDeleteCapitoloDialog(chapter)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Elimina
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
